import { PollyAtlasRoles, POLLY_ATLAS_MENU_ITEMS } from '../../constants';
import { MenuItem, MixpanelPropertiesInterface } from '../../../../@core/interfaces';
import { AtlasExplorePayload, AtlasFilteredData, AtlasTableResource, ColumnFilters, PollyAtlasRelationships } from '../interfaces';

/**
 * @description Returns list of available menu items as per the given resource level role to current user
 *
 * @param currentUserRole Current user's role for given atlas
 * @param isUserAdmin If User is SUPER_ADMIN, or ORG_ADMIN, user can perform all operations regardless of resource level role
 *
 * @returns Array of menu items available to given role, for better UX: updated the name of `manage users` to `view users`
 *          for non-manager roles to avoid confusion (and adding smaller confusion on dev side :))
 */
export function getAtlasMenuItems(currentUserRole: PollyAtlasRoles, isUserAdmin: boolean): MenuItem[] {
  if (isUserAdmin || currentUserRole === PollyAtlasRoles.MANAGER) {
    return [...POLLY_ATLAS_MENU_ITEMS];
  } else if (currentUserRole === PollyAtlasRoles.RESEARCHER) {
    return [
      ...[
        POLLY_ATLAS_MENU_ITEMS[0],
        POLLY_ATLAS_MENU_ITEMS[1],
        {
          ...POLLY_ATLAS_MENU_ITEMS[2],
          name: 'View Users',
        }
      ]
    ];
  } else if ([PollyAtlasRoles.CONTRIBUTOR, PollyAtlasRoles.CONSUMER].includes(currentUserRole)) {
    return [
      ...[
        POLLY_ATLAS_MENU_ITEMS[0],
        {
          ...POLLY_ATLAS_MENU_ITEMS[2],
          name: 'View Users',
        }
      ]
    ];
  }
  return [];
}

/**
 * @description Extracts the current user's role for given atlas from members object data
 *
 * @param currentUserId Currently logged-in user's ID
 * @param membersData Relationship object for members of atlas
 *
 * @returns User role extracted
 */
export function getCurrentUserRoleForAtlas(currentUserId: string | null, membersData: PollyAtlasRelationships): PollyAtlasRoles {
  let currentUserRole: PollyAtlasRoles = PollyAtlasRoles.CONSUMER;
  if (!!membersData) {
    membersData.permissions.data.forEach((member) => {
      if (member.attributes.principal_id === currentUserId) {
        currentUserRole = member.attributes.role;
      }
    });
  }
  return currentUserRole;
}

/**
 * @description Sanitizes a lowercase ID from input atlas id by removing special characters and replacing spaces with underscores.
 *              If the resulting ID does not start with an alphabetical character, 'atlas_' is prepended.
 * @param atlasIdInput Input value of atlas id
 * @returns atlasId Lowercase string starting with a character generated without any spaces/special characters
 *      Examples:
 *        - New Atlas for Customer @ 1 -> new_atlas_for_customer_1
 *        - 00 Customer Atlas ID with Special Chars $%#@ -> atlas_customer_id_with_special_chars_
 */
export function sanitizeAtlasId(atlasIdInput: string): string {
  const atlasId = atlasIdInput.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '_');
  return /^[a-z]/.test(atlasId) ? atlasId : 'atlas_' + atlasId;
}

export function getAtlasExplorePayload(table: AtlasTableResource, filteredData: AtlasFilteredData | undefined): AtlasExplorePayload {
  const filterStatus = filteredData ? filteredData.filterStatus : { column_filters: [], sort_by: '', sort_order: undefined };
  return {
    data: {
      type: 'explore',
      attributes: {
        columns_selected: table.attributes.columns.map(col => col.column_name),
        column_filters: filterStatus.column_filters,
        sort_by: filterStatus.sort_by,
        sort_order: filterStatus.sort_order || 'asc'
      }
    }
  };
}

export function getTableMixpanelProperties(
  atlasId: string | undefined,
  atlasName: string | undefined,
  tableName: string | undefined,
  columnName: string,
): MixpanelPropertiesInterface {
  return {
    Repository_Id: atlasId,
    Repository_Name: atlasName,
    Table_Name: tableName,
    Column_Name: columnName,
  };
}

export function checkBetweenConditionValidity(between: [number, number]): boolean {
  if (between.length !== 2) return false;
  const [min, max] = between;
  if (min === 0 && max === 0) return false;
  return typeof min === "number" && typeof max === "number" && min <= max;
}

export function findExistingFilterColumn(columnFilters: ColumnFilters[], columnName: string): { column: ColumnFilters | undefined, index: number } {
  const index = columnFilters.findIndex(col => col.column_name === columnName);
  return { column: index !== -1 ? columnFilters[index] : undefined, index };
};
